import { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
    FILTER_QUERY_KEYS,
    formatSearchParams,
    isFilterOwnedBySubPage,
    parseSearchParams,
    stripFilterQueryParams,
} from "utils/RouterManager/searchParams";
import { prefersIdInUrl } from "utils/service";

/**
 * Cleans up legacy workspace URLs that still carry ?id= or ?name=.
 * Other record types: replace ?id= with ?name= once the name is known.
 * Filter state is never kept in the URL — it lives in localStorage. On sub-pages that import
 * URL filter params themselves (see FILTER_OWNING_SUB_PAGE_FLAGS), the screen strips them after
 * the import, so they are left in place here.
 */
const useReplaceIdWithName = (id: string | undefined | null, name: string | undefined | null) => {
    const history = useHistory();
    const location = useLocation();

    useEffect(() => {
        const rawParams = parseSearchParams(location.search);
        const params = { ...rawParams };
        const hasFilterInUrl =
            FILTER_QUERY_KEYS.some((key) => key in rawParams) && !isFilterOwnedBySubPage(rawParams);
        if (hasFilterInUrl) {
            stripFilterQueryParams(params);
        }

        const replaceSearch = () => {
            const search = formatSearchParams(params);
            history.replace(search ? `${location.pathname}?${search}` : location.pathname);
        };

        if (location.pathname === "/workspace") {
            if (params.id || params.name) {
                delete params.id;
                delete params.name;
                replaceSearch();
                return;
            }
            if (hasFilterInUrl) {
                replaceSearch();
            }
            return;
        }

        const recordType = location.pathname.replace(/^\//, "");
        if (prefersIdInUrl(recordType)) {
            if (hasFilterInUrl) {
                replaceSearch();
            }
            return;
        }

        if (hasFilterInUrl) {
            replaceSearch();
            return;
        }

        if (!id || !name) return;
        if (params.id !== id) return;
        delete params.id;
        params.name = name;
        history.replace(`${location.pathname}?${formatSearchParams(params)}`);
    }, [history, id, name, location.pathname, location.search]);
};

export default useReplaceIdWithName;
